/**
 * LES ICÔNES DU POSTE DE TRAVAIL — dessinées pour le cadastre, pas importées.
 *
 * ⚠️ LES EMOJI ONT ÉTÉ RETIRÉS, ET CE N'EST PAS UNE QUESTION DE GOÛT.
 * Un emoji est rendu par la police du système : il change de dessin, de
 * couleur et de graisse selon la machine, il ne suit ni la couleur du texte ni
 * l'état d'un bouton, il ne s'aligne pas sur la grille typographique, et il
 * porte une charge expressive (🏛️, 💰) qui n'a pas sa place sur un acte
 * d'État. Sur un poste utilisé huit heures par jour, ces seize petites
 * incohérences se voient.
 *
 * ⚠️ ET CE N'EST PAS UN JEU GÉNÉRIQUE IMPORTÉ. Une icône de « maison » pour le
 * foncier ou de « dossier » pour les mutations dirait le logiciel, pas le
 * métier. Chacune dit ici ce que l'agent y fait : la mutation est un droit qui
 * passe d'un fonds à l'autre, le géomètre est une visée d'instrument, le titre
 * est un acte scellé. Un agent doit pouvoir nommer la rubrique sans lire
 * l'étiquette.
 *
 * ⚠️ `currentColor` PARTOUT, JAMAIS UNE COULEUR EN DUR. C'est ce qui les fait
 * suivre l'état — actif, désactivé, interdit — sans une seule règle de plus.
 *
 * ⚠️ AUCUNE DÉPENDANCE, AUCUN FICHIER DISTANT. Un poste de travail d'État ne
 * charge pas ses icônes chez un tiers, et une bibliothèque d'icônes pèserait
 * plus que les seize dessins qu'on en tirerait.
 *
 * Grille : 24×24, trait 1.6, extrémités et jointures arrondies. Les formes
 * tiennent dans 20×20 pour laisser une marge optique.
 */

export type NomIcone =
  | 'guichet' | 'flux' | 'plan' | 'affaires' | 'carte' | 'mutations' | 'territoire' | 'foncier'
  | 'documents' | 'geometre' | 'immobilier' | 'coproprietes' | 'fiscal'
  | 'verify' | 'prospective' | 'jumeau' | 'investment' | 'comptes' | 'atelier'
  | 'anomalies' | 'attestation' | 'livre'
  | 'sceau' | 'imprimer' | 'ouvrir' | 'chevron' | 'alerte' | 'valide' | 'refus'
  | 'assemblage' | 'capacite' | 'bureau' | 'successions' | 'instruction'
  /* D.926 · D.927 · D.930 — les trois portes d'entrée du levé. */
  | 'import' | 'cheminement' | 'terrain'
  /* D.929 — et la quatrième, qui regarde dehors : ce qui SORT vers un SIG tiers. */
  | 'ogc';

const D: Record<NomIcone, React.ReactNode> = {
  /* Guichet — le comptoir et la pièce qu'on y dépose. C'est le seul endroit
     du registre où quelqu'un se tient EN FACE d'un agent. */
  guichet: <>
    <path d="M3 20.5h18" />
    <path d="M4.5 20.5v-6.5h15v6.5" />
    <path d="M8 14v-8.5h8V14" />
    <path d="M10.5 8.5h3M10.5 11h3" />
  </>,

  /* Flux — une pièce qui se répartit entre des postes successifs. Ce sont les
     LIENS qui portent le sens, pas les boîtes : c'est le passage qu'on pilote. */
  flux: <>
    <rect x="2.5" y="9.5" width="5" height="5" rx="1" />
    <rect x="16.5" y="3.5" width="5" height="5" rx="1" />
    <rect x="16.5" y="15.5" width="5" height="5" rx="1" />
    <path d="M7.5 11.5h3.5a2 2 0 002-2V8a2 2 0 012-2h1.5" />
    <path d="M7.5 12.5h3.5a2 2 0 012 2V16a2 2 0 002 2h1.5" />
  </>,

  /* Plan de travail — les tâches du jour, cochées à mesure. */
  plan: <>
    <path d="M4 6h10M4 12h10M4 18h6" />
    <path d="M17 5.5l1.6 1.6L21.5 4" />
    <path d="M17 11.5l1.6 1.6L21.5 10" />
  </>,

  /* Dossiers — la chemise à onglet, et ce qu'elle contient. */
  affaires: <>
    <path d="M3 7.5a1.5 1.5 0 011.5-1.5h4.2l1.8 2.2h8a1.5 1.5 0 011.5 1.5v8.8a1.5 1.5 0 01-1.5 1.5h-14A1.5 1.5 0 013 18.5z" />
    <path d="M7.5 13h9M7.5 16h5.5" />
  </>,

  /* Instruction — instruire, c'est EXAMINER un feuillet : la pièce, et la
     loupe posée dessus. Volontairement distincte d'`affaires` (le dossier
     fermé, la file) et de `cheminement` (trois stations reliées, qui est un
     cheminement géodésique et non un parcours d'étapes). Le poste ouvre UNE
     affaire ; le rail doit pouvoir le dire d'un coup d'œil. */
  instruction: <>
    <path d="M6 3.5h7.5L18 8v4" />
    <path d="M6 3.5v17h5.5" />
    <path d="M13.2 3.5V8H18" />
    <path d="M9 9.5h4.5M9 12.5h3" />
    <circle cx="16.4" cy="16.4" r="3.3" />
    <path d="M18.8 18.8L21.4 21.4" />
  </>,

  /* Bureau — le plan de travail du notaire, vu de dessus : un sous-main, des
     pièces posées dessus, et la lampe. C'est un POSTE, pas un tableau de bord
     générique — et l'on doit pouvoir le nommer sans lire l'étiquette. */
  bureau: <>
    <path d="M3 18.6h18" />
    <path d="M4.6 18.6v-9.2h14.8v9.2" />
    <path d="M8 9.4V5.6h8v3.8" />
    <path d="M9.6 13h4.8M9.6 15.6h3" />
  </>,

  /* Capacité et représentation — QUI PEUT SIGNER. Une personne, une flèche,
     et le trait d'une signature : la chaîne PERSONNE → POUVOIR → ACTE, qui est
     l'objet même du §7. Volontairement DISTINCT de `comptes` (des agents du
     service) : ici il s'agit des parties à l'acte, pas des utilisateurs. */
  /**
   * SUCCESSIONS — une souche et trois branches qui en descendent.
   *
   * ⚠️ DESSINÉE, JAMAIS UN EMOJI. Le catalogue lu par le service ne connaît
   * que le NOM de l'icône ; le trait vit ici, suit la couleur du texte et
   * l'état du bouton. Et sur un acte d'État, ⚰️ porterait une charge
   * expressive qui n'a pas sa place — le dessin dit la DÉVOLUTION, pas la mort.
   */
  successions: <>
    <path d="M12 3.2v5.2" />
    <path d="M5.4 20.4v-4.2a2 2 0 012-2h9.2a2 2 0 012 2v4.2" />
    <path d="M12 8.4v6" />
    <circle cx="12" cy="3.2" r="1.6" />
    <circle cx="5.4" cy="20.4" r="1.4" />
    <circle cx="12" cy="20.4" r="1.4" />
    <circle cx="18.6" cy="20.4" r="1.4" />
  </>,

  capacite: <>
    <circle cx="6.8" cy="7.6" r="2.9" />
    <path d="M2.2 18.4v-1.1a4.6 4.6 0 019.2 0v1.1" />
    <path d="M13.6 8.2h7.2M18.4 5.8l2.4 2.4-2.4 2.4" />
    <path d="M13.8 16.6c1.4-2.1 2.8-2.1 3.7 0s2.3 2.1 3.7 0" />
  </>,

  /* Carte cadastrale — un parcellaire, pas une mappemonde : des limites
     droites qui se partagent des sommets. */
  carte: <>
    <path d="M3.5 5.5l6-2 5 2 6-2v15l-6 2-5-2-6 2z" />
    <path d="M9.5 3.5v15M14.5 5.5v15" />
  </>,

  /* Mutations — un droit qui passe d'un fonds à l'autre. Ce sont bien DEUX
     parcelles : une mutation ne modifie pas un bien, elle le transfère. */
  mutations: <>
    <rect x="3" y="4" width="7" height="7" rx="1" />
    <rect x="14" y="13" width="7" height="7" rx="1" />
    <path d="M12.5 7.5h5a2 2 0 012 2v1.5" />
    <path d="M17.5 9.2l2 2 2-2" />
  </>,

  /* Plan cadastral — le tableau d'assemblage : un territoire découpé en
     sections inégales bornées par des voies. Ce n'est pas une grille : des
     cases régulières diraient le carroyage, qui n'est que le repli. */
  assemblage: <>
    <path d="M3 4h18v16H3z" />
    <path d="M3 10h7M10 4v16M10 14h11M16 10v4M3 16h7" />
  </>,

  /* Territoire — le découpage administratif emboîté : région, préfecture,
     commune. La hiérarchie EST le sujet. */
  territoire: <>
    <path d="M3 4.5h18v15H3z" />
    <path d="M3 12h10M13 4.5v15M13 8.2h8M13 15.8h8" />
  </>,

  /* Foncier — la personne rattachée à un fonds. Le lien est le sujet, pas la
     personne seule ni la parcelle seule. */
  foncier: <>
    <circle cx="8" cy="7.5" r="3" />
    <path d="M3 19.5c0-2.8 2.2-5 5-5" />
    <path d="M12.5 12.5h8.5v8h-8.5z" />
    <path d="M12.5 16.5h8.5" />
  </>,

  /* Attestation de cession — DEUX MAINS ET UN PAPIER QUI PASSE DE L'UNE À
     L'AUTRE, devant une empreinte de pouce. C'est ce que l'acte constate : une
     cession de la main à la main, attestée devant témoins. Pas un sceau — le
     sceau dirait le titre, et une attestation NE VAUT PAS TITRE. */
  attestation: <>
    <path d="M3 12.5l3-2 4 1.5" />
    <path d="M21 12.5l-3-2-4 1.5" />
    <path d="M8.5 9.5h7v6h-7z" />
    <path d="M10.5 12h3" />
    <circle cx="12" cy="19.5" r="2.2" />
    <path d="M12 18.2v2.6" />
  </>,

  /* Livre foncier — LE REGISTRE RELIÉ ET SA FEUILLE OUVERTE, avec les traits
     des mentions inscrites au fil des rangs. Le titre foncier n'est pas un
     papier : c'est cette page-là (Art.129). */
  livre: <>
    <path d="M4 4.5h6.5a1.5 1.5 0 0 1 1.5 1.5v14a1.5 1.5 0 0 0-1.5-1.5H4z" />
    <path d="M20 4.5h-6.5A1.5 1.5 0 0 0 12 6v14a1.5 1.5 0 0 1 1.5-1.5H20z" />
    <path d="M14.5 9h3.5" />
    <path d="M14.5 12h3.5" />
    <path d="M14.5 15h2" />
  </>,

  /* Documents — l'acte et son sceau. C'est le sceau qui en fait une pièce du
     registre, pas la feuille. */
  documents: <>
    <path d="M6 3.5h8l4.5 4.5v8.5" />
    <path d="M14 3.5V8h4.5" />
    <path d="M6 3.5v17h5" />
    <circle cx="17" cy="18" r="3.2" />
    <path d="M17 16.4v1.6l1.1 1.1" />
  </>,

  /* Import SIG — un fichier qui entre : la feuille et la flèche qui y plonge.
     La flèche descend DANS la feuille, pas l'inverse : c'est un dépôt, jamais
     un export, et les deux gestes sont voisins dans un atelier. */
  import: <>
    <path d="M6 3.5h8l5 5v12a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20.5v-15A1.5 1.5 0 0 1 6.5 4z" />
    <path d="M14 3.5V9h5" />
    <path d="M12 11v6" />
    <path d="M9.5 14.5L12 17l2.5-2.5" />
  </>,
  /* Cheminement — la polygonale du géomètre : des stations reliées par des
     visées, et la boucle qui se referme. C'est la figure que l'acte décrit
     par gisements et distances. */
  /* Une source et deux arcs qui s'éloignent : ce qui SORT du registre vers un
     tiers. Volontairement l'inverse d'`import`, dont la flèche rentre. */
  ogc: <>
    <circle cx="6" cy="18" r="1.8" />
    <path d="M10.5 18a4.5 4.5 0 0 0-4.5-4.5" />
    <path d="M15.5 18a9.5 9.5 0 0 0-9.5-9.5" />
    <path d="M20.5 18A14.5 14.5 0 0 0 6 3.5" />
  </>,
  cheminement: <>
    <circle cx="5" cy="18" r="1.8" />
    <circle cx="12" cy="5.5" r="1.8" />
    <circle cx="19" cy="15" r="1.8" />
    <path d="M6.2 16.4L10.8 7.2" />
    <path d="M13.4 6.6L17.8 13.3" />
    <path d="M17.4 16.4L6.8 18" />
  </>,
  /* Terrain — la tablette revenue de brousse : l'écran et l'onde qui remonte
     quand le réseau revient. */
  terrain: <>
    <rect x="6" y="3" width="12" height="18" rx="1.8" />
    <path d="M10.5 18.2h3" />
    <path d="M9.5 10.5a3.5 3.5 0 0 1 5 0" />
    <path d="M7.5 8a6.3 6.3 0 0 1 9 0" />
  </>,
  /* Géomètre — la visée d'instrument sur un point levé : le trépied et la
     ligne de visée. */
  geometre: <>
    <circle cx="17" cy="6.5" r="2.5" />
    <path d="M4 20.5l8.5-11" />
    <path d="M4 20.5h6" />
    <path d="M7 16.5l3.5 4" />
    <path d="M14.6 8.4L20 13.5" />
  </>,

  /* Immobilier — la valeur observée dans le temps, sur du bâti. */
  immobilier: <>
    <path d="M3.5 20.5h17" />
    <path d="M5 20.5v-6l4-3 4 3v6" />
    <path d="M15 20.5V8l3-2.5L21 8v12.5" />
    <path d="M9 20.5v-3.5h0" />
  </>,

  /* Copropriétés — un immeuble dont les niveaux sont DIVISÉS : c'est la
     division en lots qui fait la copropriété. */
  coproprietes: <>
    <path d="M4.5 21V6l7.5-3 7.5 3v15" />
    <path d="M4.5 11h15M4.5 16h15" />
    <path d="M12 3v18" />
  </>,

  /* Fiscal — le fonds et l'assiette qu'on en tire. */
  fiscal: <>
    <path d="M3.5 13.5h9v7h-9z" />
    <path d="M3.5 17h9" />
    <circle cx="17" cy="8" r="4.5" />
    <path d="M17 5.8v4.4M15.4 7h3.2M15.4 9h3.2" />
  </>,

  /* Verify — la vérification publique : ce qu'un tiers peut contrôler. */
  verify: <>
    <path d="M12 3l7.5 3v6c0 4.4-3 7.7-7.5 9.5C7.5 19.7 4.5 16.4 4.5 12V6z" />
    <path d="M9 12.2l2.2 2.2L15.3 10" />
  </>,

  /* Prospective — la ville projetée : des volumes qui n'existent pas encore,
     d'où le trait interrompu. */
  prospective: <>
    <path d="M3.5 20.5h17" />
    <path d="M6 20.5V10l4-2.5V20.5" />
    <path d="M14 20.5V6l4.5 2.5v12" strokeDasharray="2.5 2.2" />
  </>,

  /* Jumeau 3D — le volume filaire : on voit les arêtes cachées, c'est ce qui
     distingue un jumeau d'une vue. */
  jumeau: <>
    <path d="M12 2.8l8.5 4.6v9.2L12 21.2 3.5 16.6V7.4z" />
    <path d="M12 12l8.5-4.6M12 12v9.2M12 12L3.5 7.4" />
  </>,

  /* Intelligence — la mesure examinée de près. */
  investment: <>
    <path d="M3.5 20.5h17" />
    <path d="M5 17.5V13M9 17.5V9M13 17.5v-2.5" />
    <circle cx="17.5" cy="8.5" r="3.8" />
    <path d="M20.3 11.3L22 13" />
  </>,

  /* Comptes — l'habilitation : une clé qui ouvre une porte précise. */
  comptes: <>
    <circle cx="8" cy="12" r="4" />
    <path d="M12 12h9M18 12v3.5M15 12v2.5" />
  </>,

  /* Atelier SIG — deux fonds mitoyens et la borne partagée qu'on déplace. */
  atelier: <>
    <path d="M3.5 5.5h8v8h-8z" />
    <path d="M11.5 9.5h9v8h-9z" />
    <circle cx="11.5" cy="9.5" r="2.2" />
  </>,

  /* ── Icônes d'action, partagées par les écrans ────────────────────────── */
  sceau: <>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 7v5l3 3" />
  </>,
  imprimer: <>
    <path d="M7 9V3.5h10V9" />
    <path d="M7 17.5H5a1.5 1.5 0 01-1.5-1.5v-5A1.5 1.5 0 015 9.5h14a1.5 1.5 0 011.5 1.5v5a1.5 1.5 0 01-1.5 1.5h-2" />
    <path d="M7 14h10v6.5H7z" />
  </>,
  ouvrir: <>
    <path d="M13.5 4.5h6v6" />
    <path d="M19.5 4.5L11 13" />
    <path d="M18 14v5.5a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 014 19.5v-11A1.5 1.5 0 015.5 7H11" />
  </>,
  chevron: <path d="M9 5l7 7-7 7" />,
  /* Anomalies — DEUX CONTOURS QUI SE CHEVAUCHENT, et la surface commune
     hachurée. C'est l'anomalie emblématique d'un cadastre : deux parcelles qui
     affirment le même terrain (11 recouvrements ouverts au 12/09/2026). Un
     triangle d'alerte aurait dit « attention » — le logiciel ; ceci dit le
     métier, et l'agent nomme la rubrique sans lire l'étiquette. */
  anomalies: <>
    <path d="M3 4.5h11v11H3z" />
    <path d="M10 8.5h11v11H10z" />
    <path d="M10.2 11.8l3.6 3.6" />
  </>,

  alerte: <>
    <path d="M12 3.5l9 16H3z" />
    <path d="M12 9.5v5M12 17.2v.3" />
  </>,
  valide: <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.2 12.3l2.6 2.6 5-5.4" />
  </>,
  refus: <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </>,
};

/**
 * ⚠️ `aria-hidden` PAR DÉFAUT, ET C'EST VOULU. Une icône qui double une
 * étiquette lue à côté ne doit pas être annoncée deux fois par un lecteur
 * d'écran. Quand l'icône est SEULE porteuse de sens, on passe `titre` — et
 * elle devient alors une image accessible, avec son nom.
 */
export function Icone(
  { nom, taille = 18, titre, className }:
  { nom: NomIcone; taille?: number; titre?: string; className?: string },
) {
  return (
    <svg
      className={className}
      width={taille} height={taille} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={1.6}
      strokeLinecap="round" strokeLinejoin="round"
      role={titre ? 'img' : undefined}
      aria-hidden={titre ? undefined : true}
      aria-label={titre}
      focusable="false"
    >
      {titre && <title>{titre}</title>}
      {D[nom]}
    </svg>
  );
}

/** Les clés connues — pour que le catalogue des rubriques soit vérifiable. */
export const ICONES_CONNUES = Object.keys(D) as NomIcone[];
